import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref as dbRef, onValue, off, set, remove } from "firebase/database";
import { db, rtdb } from "../../../firebase"; // ← adjust
import type { AppDispatch, RootState } from "../../../redux/store";
import {
  upsertDriver, deleteDriver, listenToDriverData, setActiveVehicle,
  selectListings,
} from "../../../redux/fleetSlice";

import { listenToUserCommercePartners } from "../../../redux/commercePartnersSlice";


import {
  Plus, Eye, EyeOff, Trash2, Edit3, Search,
  Car, MapPin, Tag, GitMerge, Package, Image as ImageIcon,
  CheckCircle, X, Loader2, RefreshCw, AlertCircle,
  ChevronLeft, ChevronRight, ChevronDown, DollarSign, Sparkles, Circle, CheckCircle2,
} from "lucide-react";
import {
  BIKE_TYPES, CAR_TYPES, JET_TYPES, FUEL_TYPES, TRANSMISSION_TYPES,
  RIDE_TYPES, RIDE_TYPE, vehicleTypesForRideType,
  CAR_BRANDS, carBrandName,
  CLASS_TYPES, CLASS_TYPE, classTypeName,
  CAR_SIZES, CAR_SIZE, carSizeName,
  fuelTypeName, transmissionName,
} from "../../../CATEGORIES/OperatorCategories";

// ✅ cosmoola currency (same as ShortListings)
import { useCurrency } from "@cosmoola/react-cosmoola-js";

/* ─── Vehicle types ──────────────────────────────────────────────────────── */
const ALL_VEHICLE_TYPES = [...(CAR_TYPES as any[]), ...(BIKE_TYPES as any[]), ...(JET_TYPES as any[])];


type RRT =
  | "perSurge" | "perKm" | "perPerson" | "perPersonPerKm" | "perDay" | "perCar"
  | "perSeat"  | "perMinute" | "perHour" | "perWeek" | "perMonth" | "perYear";
type DK  = "none" | "is5" | "is10" | "is25" | "is50" | "is80";

const RATE_OPTS: { type: RRT; label: string; unit: string; sub?: string }[] = [
  { type: "perKm",           label: "Per KM",           unit: "/km",          sub: "Metered by distance — recommended" },
  { type: "perSurge",        label: "System Default",   unit: "surge"        },
  { type: "perMinute",       label: "Per Minute",       unit: "/min"         },
  { type: "perHour",         label: "Per Hour",         unit: "/hour"        },
  { type: "perCar",          label: "Per Car",          unit: "flat"         },
  { type: "perPerson",       label: "Per Person",       unit: "/person"      },
  { type: "perPersonPerKm",  label: "Per Person · KM",  unit: "/person/km"   },
  { type: "perSeat",         label: "Per Seat",         unit: "/seat"        },
  { type: "perDay",          label: "Per Day",          unit: "/day"         },
  { type: "perWeek",         label: "Per Week",         unit: "/week"        },
  { type: "perMonth",        label: "Per Month",        unit: "/month"       },
  { type: "perYear",         label: "Per Year",         unit: "/year"        },
];

/* ── Rate-type routing — mirrors RiderHome's rateCategoryOf so the driver
      sets exactly the cap the rider-side booking modal will read. ── */
type RateCategory = "people" | "duration" | "metered";
const PEOPLE_RATE_TYPES = new Set<RRT>(["perPerson", "perPersonPerKm", "perSeat"]);
const DURATION_RATE_TYPES = new Set<RRT>(["perDay", "perWeek", "perMonth", "perYear"]);
const rateCategoryOf = (rt: RRT): RateCategory => {
  if (PEOPLE_RATE_TYPES.has(rt)) return "people";
  if (DURATION_RATE_TYPES.has(rt)) return "duration";
  return "metered";
};
const DURATION_UNIT_LABEL: Partial<Record<RRT, string>> = {
  perDay: "day", perWeek: "week", perMonth: "month", perYear: "year",
};
const DURATION_FIELD_KEY: Partial<Record<RRT, "maxDays" | "maxWeeks" | "maxMonths" | "maxYears">> = {
  perDay: "maxDays", perWeek: "maxWeeks", perMonth: "maxMonths", perYear: "maxYears",
};

const DISC_OPTS: { key: DK; label: string }[] = [
  { key: "none", label: "None" }, { key: "is5",  label: "5%" },
  { key: "is10", label: "10%" }, { key: "is25", label: "25%" },
  { key: "is50", label: "50%" }, { key: "is80", label: "80%" },
];

const DRIVER_ONLINE  = "https://europe-west1-eartlas.cloudfunctions.net/driverOnline";
const DRIVER_OFFLINE = "https://europe-west1-eartlas.cloudfunctions.net/driverOffline";
const DRIVER_PING    = "https://europe-west1-eartlas.cloudfunctions.net/driverPing";
/* ─── Heartlas design tokens (same family as DriverDashboard) ────────────── */
const H = {
  bg:        "#FBF6EE",
  panel:     "#FFFFFF",
  panelSoft: "#FCEFD4",
  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",
  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",
  green:     "#1DB954",
  greenSoft: "#E5F8ED",
  amber:     "#D4900F",
  amberSoft: "#FCEFD4",
  blue:      "#3D7BD9",
  blueSoft:  "#EAF1FC",
  border:    "#EEDFC4",
};

/* ─── Wizard steps ───────────────────────────────────────────────────────── */
const STEPS = [
  { key: "vehicle",  label: "Vehicle",  Icon: Car },
  { key: "pricing",  label: "Pricing",  Icon: DollarSign },
  { key: "location", label: "Location", Icon: MapPin },
] as const;

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function discKeyFromObj(obj: Record<string, boolean> = {}): DK {
  if (obj.is80) return "is80"; if (obj.is50) return "is50";
  if (obj.is25) return "is25"; if (obj.is10) return "is10";
  if (obj.is5)  return "is5";  return "none";
}
function buildDiscount(key: DK): Record<string, boolean> {
  return { is5: key === "is5", is10: key === "is10", is25: key === "is25", is50: key === "is50", is80: key === "is80" };
}
function extrasToArr(obj: Record<string, number> = {}): { name: string; price: number }[] {
  return Object.entries(obj).map(([name, price]) => ({ name, price }));
}
function extrasToObj(arr: { name: string; price: number }[]): Record<string, number> {
  return arr.reduce((a, e) => { a[e.name] = e.price; return a; }, {} as Record<string, number>);
}
function vehicleName(id: any): string {
  return (ALL_VEHICLE_TYPES.find((v) => v.id === id) as any)?.name ?? "Vehicle";
}
function rateUnit(type: RRT): string {
  return RATE_OPTS.find((r) => r.type === type)?.unit ?? "";
}

/** Full category item (icon + optional image) for a given vehicleType id —
    used by RowThumb below to pick the right icon/image per ride type. */
function vehicleTypeItem(id: any): any {
  return ALL_VEHICLE_TYPES.find((v: any) => v.id === id);
}

/* Reusable numeric field: real <input type="number"> plus explicit − / +
   buttons. Defined once at module scope so it never remounts on keystroke. */
function NumberStepper({
  value, onChange, min = 0, max = 999999, step = 1, placeholder, compact = false,
}: {
  value: string | number; onChange: (val: string) => void;
  min?: number; max?: number; step?: number; placeholder?: string; compact?: boolean;
}) {
  const numeric = typeof value === "number" ? value : parseFloat(value as string) || 0;
  const bump = (delta: number) => {
    const next = Math.min(max, Math.max(min, parseFloat((numeric + delta).toFixed(2))));
    onChange(String(next));
  };
  return (
    <div className={`dc-num-field${compact ? " dc-num-field-compact" : ""}`}>
      <button type="button" className="dc-num-btn" onClick={() => bump(-step)} aria-label="Decrease">−</button>
      <input
        className="dc-num-input" type="number" inputMode="decimal"
        value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      />
      <button type="button" className="dc-num-btn" onClick={() => bump(step)} aria-label="Increase">+</button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   PickerField — replaces the old horizontal chip-scrollers for every
   category selector (ride type, vehicle type, brand, class, size, fuel,
   transmission, billing type). Renders a single button showing the current
   selection; tapping it opens a radio-list picker that renders as a bottom
   sheet on mobile and a floating centered card ("dropdown") on desktop —
   same responsive shell CommerceOnboarding / the old billing-type modal
   already used. Defined at module scope so it's a stable component and
   never remounts (and therefore never loses the open/closed state) when
   the parent re-renders on every keystroke elsewhere in the form.
───────────────────────────────────────────────────────────────────────── */
type PickerOption = { id: any; name: string; color?: string; icon?: React.ReactNode; sub?: string; badge?: string };

function PickerField({
  label, value, options, onSelect, placeholder = "Select…", required,
}: {
  label: string;
  value: any;
  options: PickerOption[];
  onSelect: (id: any) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);
  return (
    <>
      <label className="dc-form-lbl">{label}{required && <span style={{ color: H.red }}> *</span>}</label>
      <button type="button" className="dc-rate-select-btn" onClick={() => setOpen(true)}>
        <span className="dc-rate-select-main">
          {selected?.color && <span className="dc-brand-dot" style={{ background: selected.color }} />}
          {selected?.icon}
          {selected ? selected.name : placeholder}
          {selected?.badge && <span className="dc-rate-select-badge">{selected.badge}</span>}
        </span>
        <span className="dc-rate-select-chevron"><ChevronDown size={14} /></span>
      </button>

      {open && (
        <div className="dc-modal-backdrop" onClick={() => setOpen(false)}>
          <div className="dc-modal dc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="dc-mob-handle" />
            <div className="dc-modal-header">
              <div className="dc-modal-title">{label}</div>
              <button className="dc-modal-close" onClick={() => setOpen(false)}><X size={15} /></button>
            </div>
            <div className="dc-modal-scroll">
              {options.map((o) => (
                <div
                  key={String(o.id)}
                  className="dc-radio-row"
                  onClick={() => { onSelect(o.id); setOpen(false); }}
                >
                  <div>
                    <div className="dc-radio-main">
                      {o.color && <span className="dc-brand-dot" style={{ background: o.color }} />}
                      {o.icon}
                      <span className="dc-radio-lbl">{o.name}</span>
                      {o.badge && <span className="dc-rate-select-badge">{o.badge}</span>}
                    </div>
                    {o.sub && <div className="dc-radio-sub">{o.sub}</div>}
                  </div>
                  <div className={`dc-radio-circle${value === o.id ? " on" : ""}`} />
                </div>
              ))}
              {options.length === 0 && (
                <div style={{ padding: "20px 4px", textAlign: "center", fontSize: 12, color: H.textFaint }}>
                  Nothing to choose here yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   RowThumb — resolves what to show for a listing's thumbnail, tried in
   order:
     1. an actual photo the driver uploaded for this vehicle
     2. a stock image for the vehicle TYPE, if one has been set on the
        matching entry in OperatorCategories (CategoryItem already has an
        optional `image` field — it's just unpopulated today. Set a URL on
        any CAR_TYPES / BIKE_TYPES / JET_TYPES entry and it lights up here
        automatically, the same way hostIcon/bedIcon etc. are used
        elsewhere in the app)
     3. that vehicle type's lucide icon, which always exists as the floor

   Each <img> carries its own onError so a broken/expired URL falls
   straight through to the next option instead of leaving a blank box —
   this is the same pattern FactChip uses in ShortAccommodationDetails.
───────────────────────────────────────────────────────────────────────── */
function RowThumb({ listing }: { listing: any }) {
  const typeItem = vehicleTypeItem(listing.vehicleType);
  const rideItem = RIDE_TYPES.find((r: any) => r.id === listing.rideType);
  const photo    = listing.photos?.[0];
  const typeImg  = typeItem?.image;
  const rideImg  = rideItem?.image;

  const [photoFailed, setPhotoFailed]     = useState(false);
  const [typeImgFailed, setTypeImgFailed] = useState(false);
  const [rideImgFailed, setRideImgFailed] = useState(false);

  if (photo && !photoFailed) {
    return <img src={photo} alt="" onError={() => setPhotoFailed(true)} />;
  }
  if (typeImg && !typeImgFailed) {
    return <img src={typeImg} alt="" onError={() => setTypeImgFailed(true)} />;
  }
  if (rideImg && !rideImgFailed) {
    return <img src={rideImg} alt="" onError={() => setRideImgFailed(true)} />;
  }
  return (
    <div className="dc-row-thumb-ph">
      {typeItem?.icon ?? <Car size={30} />}
    </div>
  );
}

/* Small labelled fact — "Fuel: Petrol" style, used in the spec grid so real
   vehicle details show as readable text instead of only icon badges. */
function SpecItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="dc-spec-item">
      <span className="dc-spec-lbl">{label}</span>
      <span className="dc-spec-val">{value}</span>
    </div>
  );
}

function blankForm() {
  return {
    rideTypeId:     RIDE_TYPE.CAR,
    vehicleTypeId:  (CAR_TYPES as any[])[0]?.id,
    brandId:        (CAR_BRANDS as any[])[0]?.id,
    classTypeId:    CLASS_TYPE.ECONOMY,
    carSizeId:      CAR_SIZE.M,
    fuelTypeId:     (FUEL_TYPES as any[])[0]?.id,
    transmissionId: (TRANSMISSION_TYPES as any[])[0]?.id,
    model:          "",
    licensePlate:   "",
    year:           "",
    capacity:       "4",
    description:    "",
    rate:           5,
rateType:       "perKm" as RRT,
    discountKey:    "none" as DK,
    extras:         [] as { name: string; price: number }[],
    guestCount:     "4",   // max people/seats — only relevant for perPerson · perPersonPerKm · perSeat
    maxDays:        "30",
    maxWeeks:       "12",
    maxMonths:      "12",
    maxYears:       "2",
    city:           "",
    country:        "DE",
    serviceRadiusKm:"15",
    allowParcel:    false,
    rideSharing:    false,
    isPublished:    false,
  };
}
type EditForm = ReturnType<typeof blankForm>;

function formFromListing(listing: any): EditForm {
  return {
    rideTypeId:      listing.rideType     ?? RIDE_TYPE.CAR,
    vehicleTypeId:   listing.vehicleType  ?? (CAR_TYPES as any[])[0]?.id,
    brandId:         listing.brand        ?? (CAR_BRANDS as any[])[0]?.id,
    classTypeId:     listing.classType    ?? CLASS_TYPE.ECONOMY,
    carSizeId:       listing.carSize      ?? CAR_SIZE.M,
    fuelTypeId:      listing.fuelType     ?? (FUEL_TYPES as any[])[0]?.id,
    transmissionId:  listing.transmission ?? (TRANSMISSION_TYPES as any[])[0]?.id,
    model:           listing.model         ?? "",
    licensePlate:    listing.licensePlate  ?? "",
    year:            String(listing.year   ?? ""),
    capacity:        String(listing.capacity ?? "4"),
    description:     listing.description   ?? "",
    rate:            listing.rate           ?? 5,
    rateType:        listing.rateType       ?? "perKm",
    discountKey:     discKeyFromObj(listing.discountApplied),
    extras:          extrasToArr(listing.extras),
    guestCount:      String(listing.guestCount ?? listing.capacity ?? "4"),
    maxDays:         String(listing.maxDays   ?? "30"),
    maxWeeks:        String(listing.maxWeeks  ?? "12"),
    maxMonths:       String(listing.maxMonths ?? "12"),
    maxYears:        String(listing.maxYears  ?? "2"),
    city:            listing.city ?? listing.address?.city ?? "",
    country:         listing.country ?? listing.address?.country ?? "KE",
    serviceRadiusKm: String(listing.serviceRadiusKm ?? "15"),
    allowParcel:     listing.allowParcel ?? false,
    rideSharing:     listing.rideSharing ?? false,
    isPublished:     listing.isPublished ?? false,
  };
}

/* ─── CSS — matches DriverDashboard's cream/gold/red theme, scoped to .dc-root ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;700;800&family=Cormorant+Garamond:wght@300;400;600;700&family=Montserrat:wght@400;500;600;700;800;900&display=swap');
.dc-root, .dc-root *, .dc-root *::before, .dc-root *::after{box-sizing:border-box;}
.dc-root{
  --serif:'Baloo 2','Cormorant Garamond',Georgia,serif;--sans:'Montserrat',-apple-system,sans-serif;
  width:100%;max-width:1360px;margin:0 auto;font-family:var(--sans);color:${H.dark};
}
.dc-root button{font-family:inherit;}
.dc-root ::-webkit-scrollbar{width:8px;height:8px;}
.dc-root ::-webkit-scrollbar-thumb{background:${H.border};border-radius:8px;}

.dc-rev{opacity:0;transform:translateY(10px);transition:opacity .4s ease var(--d,0ms),transform .4s cubic-bezier(.22,1,.36,1) var(--d,0ms);}
.dc-on .dc-rev{opacity:1;transform:none;}

/* ── header ── */
.dc-header{display:flex;align-items:center;gap:10px;margin-bottom:14px;}
.dc-header-text{flex:1;min-width:0;}
.dc-header-eye{font-size:9px;font-weight:800;letter-spacing:2px;color:${H.goldDark};text-transform:uppercase;margin-bottom:3px;}
.dc-header-title{font-family:var(--serif);font-size:24px;font-weight:700;color:${H.dark};}
.dc-icon-btn{width:34px;height:34px;border-radius:50%;background:${H.panelSoft};border:1px solid ${H.border};display:flex;align-items:center;justify-content:center;cursor:pointer;color:${H.goldDark};flex-shrink:0;transition:background .2s,color .2s,transform .15s;}
.dc-icon-btn:hover{background:${H.gold};color:#fff;}
.dc-icon-btn:active{transform:translateY(1px);}
.dc-add-btn{display:flex;align-items:center;gap:6px;background:${H.red};padding:9px 16px;cursor:pointer;color:#fff;font-size:11px;font-weight:800;letter-spacing:.5px;border:none;font-family:var(--sans);border-radius:999px;box-shadow:0 4px 12px rgba(200,32,46,.28);transition:filter .2s,transform .15s;flex-shrink:0;}
.dc-add-btn:hover{filter:brightness(1.08);}
.dc-add-btn:active{transform:translateY(1px);}
.dc-spin{animation:dcSpin .8s linear infinite;}

/* ── search bar ── */
.dc-search{display:flex;align-items:center;gap:8px;background:${H.panel};border:1px solid ${H.border};border-radius:14px;padding:10px 14px;margin-bottom:16px;box-shadow:0 2px 8px rgba(27,14,6,.04);}
.dc-search input{flex:1;border:none;outline:none;background:transparent;font-size:13px;font-family:var(--sans);color:${H.dark};}
.dc-search input::placeholder{color:${H.textFaint};}
.dc-search svg{color:${H.textFaint};flex-shrink:0;}
.dc-search-clear{background:none;border:none;cursor:pointer;color:${H.textFaint};display:flex;flex-shrink:0;}
.dc-search-clear:hover{color:${H.red};}

/* ── stats strip ── */
.dc-stats{display:flex;gap:0;border:1px solid ${H.border};border-radius:16px;overflow:hidden;margin-bottom:20px;background:${H.panel};}
.dc-stat-item{flex:1;display:flex;flex-direction:column;align-items:center;gap:2px;padding:12px 6px;border-right:1px solid ${H.border};}
.dc-stat-item:last-child{border-right:none;}
.dc-stat-val{font-family:var(--serif);font-size:24px;font-weight:700;}
.dc-stat-lbl{font-size:8px;font-weight:800;letter-spacing:1.2px;color:${H.textFaint};text-transform:uppercase;}

/* ── listing grid — 1 col mobile, 2 col tablet, 3 col desktop. Cards size
   to their grid track instead of stretching edge-to-edge on wide screens,
   which is why a lone card used to look like it was expanding with the
   browser window. ── */
.dc-list{display:grid;grid-template-columns:1fr;gap:20px;}
@media(min-width:640px){.dc-list{grid-template-columns:repeat(2,1fr);}}
@media(min-width:1040px){.dc-list{grid-template-columns:repeat(3,1fr);}}

.dc-row-card{background:${H.panel};border:2px solid transparent;border-radius:20px;box-shadow:0 4px 16px rgba(27,14,6,.06);transition:box-shadow .25s ease,transform .25s cubic-bezier(.22,1,.36,1),border-color .2s;overflow:hidden;display:flex;flex-direction:column;}
.dc-row-card:hover{box-shadow:0 14px 30px rgba(27,14,6,.12);transform:translateY(-4px);}
.dc-row-card.dc-card-active{border-color:${H.green};box-shadow:0 0 0 3px ${H.greenSoft},0 10px 26px rgba(27,14,6,.10);}
.dc-row-card.dc-card-active:hover{box-shadow:0 0 0 3px ${H.greenSoft},0 16px 32px rgba(27,14,6,.14);}

.dc-row-head{display:flex;align-items:center;gap:14px;padding:14px;cursor:pointer;}

.dc-row-thumb{width:84px;height:84px;border-radius:16px;overflow:hidden;flex-shrink:0;position:relative;background:linear-gradient(135deg,${H.panelSoft},${H.bg});}
.dc-row-thumb img{width:100%;height:100%;object-fit:cover;display:block;}
.dc-row-thumb-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:${H.goldDark};background:linear-gradient(135deg,${H.panelSoft},${H.bg});}
.dc-row-thumb-ph svg{width:30px;height:30px;opacity:.5;}
.dc-row-active-dot{position:absolute;top:4px;right:4px;width:10px;height:10px;border-radius:50%;background:${H.green};border:2px solid #fff;z-index:2;}

.dc-row-meta{flex:1;min-width:0;}
.dc-row-title{font-family:var(--serif);font-size:17px;font-weight:700;color:${H.dark};margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.dc-row-sub{font-size:12px;color:${H.textFaint};margin-bottom:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

.dc-status-pill{display:inline-flex;align-items:center;gap:7px;background:${H.bg};padding:5px 12px 5px 10px;border-radius:999px;font-size:12px;font-weight:600;color:${H.textMuted};}
.dc-status-pill-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}

.dc-row-side{display:flex;flex-direction:column;align-items:flex-end;gap:6px;flex-shrink:0;}
.dc-row-rate{font-family:var(--serif);font-size:16px;font-weight:700;color:${H.redDark};}
.dc-row-rate-unit{font-size:9px;color:${H.goldDark};font-weight:700;text-align:right;}
.dc-row-chevron{color:${H.textFaint};display:flex;transition:transform .3s cubic-bezier(.22,1,.36,1);}
.dc-row-chevron.open{transform:rotate(180deg);}

/* "open like a book" expand panel — grid-rows trick for smooth height,
   plus a subtle unfold (scaleY + rotateX) on the inner content so it
   feels like a page lifting open rather than a flat accordion. Because
   the card itself has overflow:hidden, the unfold never spills past the
   card's own rounded corners or pushes on neighbouring grid cells. */
.dc-row-expand-wrap{display:grid;grid-template-rows:0fr;transition:grid-template-rows .4s cubic-bezier(.22,1,.36,1);}
.dc-row-expand-wrap.open{grid-template-rows:1fr;}
.dc-row-expand-inner{overflow:hidden;min-height:0;}
.dc-row-expand-page{transform-origin:top center;transform:scaleY(.97) rotateX(-6deg);opacity:0;transition:transform .38s cubic-bezier(.22,1,.36,1),opacity .3s ease;padding:0 16px 16px;border-top:1px solid ${H.border};margin-top:2px;}
.dc-row-expand-wrap.open .dc-row-expand-page{transform:scaleY(1) rotateX(0deg);opacity:1;}

/* ── spec grid — actual labelled facts ("Fuel: Petrol"), not just badges ── */
.dc-spec-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin:14px 0 4px;}
.dc-spec-item{display:flex;flex-direction:column;gap:2px;background:${H.bg};border:1px solid ${H.border};border-radius:12px;padding:8px 11px;}
.dc-spec-lbl{font-size:8.5px;font-weight:800;letter-spacing:1px;color:${H.textFaint};text-transform:uppercase;}
.dc-spec-val{font-size:13px;font-weight:700;color:${H.dark};font-family:var(--sans);}

.dc-meta-row{display:flex;flex-wrap:wrap;gap:5px;margin:4px 0 10px;}
.dc-pill{display:flex;align-items:center;gap:4px;padding:3px 9px;border:1px solid;font-size:9px;font-weight:700;border-radius:20px;}
.dc-extras-row{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:10px;}
.dc-extra-tag{background:${H.bg};border:1px solid ${H.border};padding:2px 8px;font-size:9px;font-weight:600;color:${H.textMuted};border-radius:8px;}
.dc-loc-row{display:flex;align-items:center;gap:5px;margin-bottom:12px;font-size:10px;color:${H.textMuted};}
.dc-desc-row{font-size:12px;color:${H.textMuted};line-height:1.6;margin-bottom:12px;}

/* ── active-vehicle radio row (Uber-Fleet-style switcher) ── */
.dc-active-row{display:flex;align-items:center;justify-content:space-between;padding:9px 0 12px;border-top:1px dashed ${H.border};margin-top:2px;margin-bottom:10px;}
.dc-active-btn{display:flex;align-items:center;gap:7px;cursor:pointer;background:none;border:none;padding:0;}
.dc-active-circle{width:17px;height:17px;border-radius:50%;border:2px solid ${H.border};position:relative;flex-shrink:0;transition:border-color .15s;}
.dc-active-circle.on{border-color:${H.green};}
.dc-active-circle.on::after{content:"";position:absolute;inset:2.5px;border-radius:50%;background:${H.green};}
.dc-active-label{font-size:10.5px;font-weight:700;color:${H.textFaint};}
.dc-active-label.on{color:${H.green};}

.dc-card-actions{display:flex;gap:8px;}
.dc-edit-btn{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;background:${H.goldSoft};border:1px solid ${H.gold};padding:9px 0;cursor:pointer;font-size:10px;font-weight:800;letter-spacing:.6px;color:${H.goldDark};font-family:var(--sans);border-radius:12px;transition:background .2s,transform .15s;}
.dc-edit-btn:hover{background:${H.gold};color:#fff;}
.dc-edit-btn:active{transform:translateY(1px);}
.dc-del-btn{width:38px;display:flex;align-items:center;justify-content:center;background:${H.redSoft};border:1px solid #F3B9BE;cursor:pointer;color:${H.redDark};border-radius:12px;transition:background .2s,transform .15s;}
.dc-del-btn:hover{background:#F6CDD1;}
.dc-del-btn:active{transform:translateY(1px);}

/* ── empty / loading ── */
.dc-empty{display:flex;flex-direction:column;align-items:center;gap:12px;padding:60px 24px;text-align:center;background:${H.panel};border:1px solid ${H.border};border-radius:18px;}
.dc-empty-icon{color:${H.goldDark};margin-bottom:4px;}
.dc-empty-title{font-family:var(--serif);font-size:23px;font-weight:700;color:${H.dark};}
.dc-empty-sub{font-size:12px;color:${H.textMuted};line-height:1.6;max-width:280px;}
.dc-empty-btn{display:flex;align-items:center;gap:6px;background:${H.red};padding:10px 18px;cursor:pointer;color:#fff;font-size:11px;font-weight:800;font-family:var(--sans);border:none;border-radius:999px;box-shadow:0 4px 12px rgba(200,32,46,.28);transition:transform .15s;}
.dc-empty-btn:active{transform:translateY(1px);}
.dc-loader{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:60px 0;}
.dc-loader-txt{color:${H.textMuted};font-size:12px;letter-spacing:.4px;}

/* ══════════════════════════════════════════════════════════
   MODAL — floating card on desktop, bottom sheet on mobile.
══════════════════════════════════════════════════════════ */
.dc-modal-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.42);backdrop-filter:blur(3px);z-index:300;display:flex;}
.dc-modal{background:${H.panel};display:flex;flex-direction:column;width:100%;overflow:hidden;}
@media(min-width:960px){
  .dc-modal-backdrop{align-items:center;justify-content:center;padding:24px;}
  .dc-modal{max-width:460px;max-height:86vh;border-radius:22px;box-shadow:0 30px 70px rgba(27,14,6,.35);animation:dcPopIn .26s cubic-bezier(.22,1,.36,1) both;}
}
@media(max-width:959px){
  .dc-modal-backdrop{align-items:flex-end;}
  .dc-modal{max-height:92vh;border-top-left-radius:24px;border-top-right-radius:24px;animation:dcSlideUp .3s cubic-bezier(.22,1,.36,1) both;}
  .dc-mob-handle{width:38px;height:4px;background:${H.border};border-radius:2px;margin:10px auto 0;flex-shrink:0;}
}
@media(min-width:960px){.dc-mob-handle{display:none;}}

.dc-modal-header{display:flex;align-items:center;justify-content:space-between;padding:16px 18px 12px;border-bottom:1px solid ${H.border};flex-shrink:0;}
.dc-modal-title{font-family:var(--serif);font-size:18px;font-weight:700;color:${H.dark};}
.dc-modal-close{width:32px;height:32px;border-radius:50%;background:${H.panelSoft};border:1px solid ${H.border};display:flex;align-items:center;justify-content:center;cursor:pointer;color:${H.textMuted};flex-shrink:0;}
.dc-modal-close:hover{background:${H.gold};color:#fff;}

/* step progress */
.dc-steps{display:flex;align-items:center;gap:6px;padding:14px 18px 4px;flex-shrink:0;}
.dc-step-dot{flex:1;display:flex;align-items:center;gap:6px;}
.dc-step-circle{width:24px;height:24px;border-radius:50%;background:${H.panelSoft};border:1.5px solid ${H.border};display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:800;color:${H.textFaint};flex-shrink:0;transition:all .2s;}
.dc-step-circle.done{background:${H.green};border-color:${H.green};color:#fff;}
.dc-step-circle.active{background:${H.red};border-color:${H.red};color:#fff;box-shadow:0 0 0 4px ${H.redSoft};}
.dc-step-label{font-size:10.5px;font-weight:700;color:${H.textFaint};}
.dc-step-label.active{color:${H.dark};}
.dc-step-line{flex:1;height:2px;background:${H.border};margin:0 2px;border-radius:2px;}
.dc-step-line.done{background:${H.green};}

.dc-modal-scroll{flex:1;overflow-y:auto;padding:16px 18px;}
.dc-modal-footer{display:flex;gap:10px;padding:14px 18px;border-top:1px solid ${H.border};flex-shrink:0;}
.dc-btn-ghost{display:flex;align-items:center;justify-content:center;gap:5px;flex:1;padding:12px 0;background:${H.panelSoft};border:1px solid ${H.border};cursor:pointer;color:${H.textMuted};font-size:11px;font-weight:800;letter-spacing:.5px;font-family:var(--sans);border-radius:999px;transition:background .2s;}
.dc-btn-ghost:hover{background:${H.border};}
.dc-btn-primary{display:flex;align-items:center;justify-content:center;gap:6px;flex:2;padding:12px 0;background:${H.red};border:none;cursor:pointer;color:#fff;font-size:11px;font-weight:800;letter-spacing:.5px;font-family:var(--sans);border-radius:999px;box-shadow:0 4px 12px rgba(200,32,46,.28);transition:filter .2s,transform .15s;}
.dc-btn-primary:hover{filter:brightness(1.06);}
.dc-btn-primary:active{transform:translateY(1px);}
.dc-btn-primary:disabled{opacity:.5;cursor:not-allowed;filter:none;}

/* form bits inside the wizard */
.dc-step-eye{display:flex;align-items:center;gap:6px;font-size:10px;font-weight:800;letter-spacing:1.6px;color:${H.goldDark};text-transform:uppercase;margin-bottom:14px;}
.dc-form-lbl{font-size:9.5px;font-weight:800;letter-spacing:1px;color:${H.textMuted};text-transform:uppercase;margin-bottom:6px;display:block;}
.dc-input{width:100%;background:${H.bg};border:1px solid ${H.border};padding:11px 13px;color:${H.dark};font-size:13.5px;font-family:var(--sans);outline:none;margin-bottom:12px;border-radius:12px;transition:border-color .2s;}
.dc-input:focus{border-color:${H.gold};}
.dc-input::placeholder{color:${H.textFaint};}
.dc-textarea{width:100%;background:${H.bg};border:1px solid ${H.border};padding:11px 13px;color:${H.dark};font-size:13.5px;font-family:var(--sans);outline:none;resize:none;margin-bottom:12px;border-radius:12px;transition:border-color .2s;}
.dc-textarea:focus{border-color:${H.gold};}
.dc-form-row{display:grid;grid-template-columns:1.6fr 1fr;gap:10px;}

.dc-chips{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px;}
.dc-chip{padding:7px 13px;border:1px solid ${H.border};background:${H.bg};cursor:pointer;font-size:10.5px;font-weight:700;color:${H.textMuted};font-family:var(--sans);border-radius:999px;transition:all .15s;}
.dc-chip:hover{border-color:${H.gold};}
.dc-chip.on{background:${H.goldSoft};border-color:${H.gold};color:${H.goldDark};}

/* picker-field select button (opens the radio modal) — reused for every category */
.dc-rate-select-btn{width:100%;display:flex;align-items:center;justify-content:space-between;background:${H.bg};border:1px solid ${H.border};padding:12px 13px;color:${H.dark};font-size:13.5px;font-weight:700;font-family:var(--sans);cursor:pointer;margin-bottom:12px;border-radius:12px;transition:border-color .2s;}
.dc-rate-select-btn:hover{border-color:${H.gold};}
.dc-rate-select-main{display:flex;align-items:center;gap:8px;}
.dc-rate-select-badge{font-size:8px;font-weight:800;letter-spacing:.5px;color:${H.goldDark};background:${H.goldSoft};border:1px solid ${H.gold};padding:2px 7px;border-radius:999px;text-transform:uppercase;}
.dc-rate-select-chevron{color:${H.textFaint};display:flex;}

/* radio rows inside picker modals */
.dc-radio-row{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:13px 4px;border-bottom:1px solid ${H.border};cursor:pointer;}
.dc-radio-row:last-child{border-bottom:none;}
.dc-radio-main{display:flex;align-items:center;gap:7px;flex-wrap:wrap;}
.dc-radio-lbl{font-size:13.5px;font-weight:700;color:${H.dark};}
.dc-radio-sub{font-size:10.5px;color:${H.textFaint};margin-top:2px;flex-basis:100%;}
.dc-radio-circle{width:19px;height:19px;border-radius:50%;border:2px solid ${H.border};flex-shrink:0;position:relative;transition:border-color .15s;}
.dc-radio-circle.on{border-color:${H.red};}
.dc-radio-circle.on::after{content:"";position:absolute;inset:3px;border-radius:50%;background:${H.red};}

.dc-modal-sm{max-width:380px;}

.dc-rate-big{display:flex;align-items:flex-end;justify-content:center;gap:4px;margin:6px 0 12px;padding:16px;background:${H.goldSoft};border:1px solid ${H.gold};border-radius:16px;}
.dc-rate-big-curr{font-size:20px;color:${H.goldDark};padding-bottom:7px;}
.dc-rate-big-val{font-family:var(--serif);font-size:46px;font-weight:700;color:${H.redDark};line-height:1;}
.dc-rate-big-unit{font-size:13px;color:${H.goldDark};padding-bottom:9px;}
.dc-stepper{display:flex;align-items:center;gap:5px;margin-bottom:14px;}
.dc-step-btn{background:${H.bg};border:1px solid ${H.border};padding:8px 10px;cursor:pointer;font-size:10.5px;font-weight:700;color:${H.textMuted};font-family:var(--sans);min-width:38px;text-align:center;border-radius:10px;transition:background .15s;}
.dc-step-btn:hover{background:${H.goldSoft};}
.dc-step-input{flex:1;background:#fff;border:1px solid ${H.gold};padding:9px 0;color:${H.redDark};font-size:18px;font-weight:800;text-align:center;font-family:var(--serif);outline:none;border-radius:10px;}

.dc-num-field{display:flex;align-items:center;gap:6px;margin-bottom:12px;}
.dc-num-btn{width:36px;height:36px;flex-shrink:0;background:${H.bg};border:1px solid ${H.border};color:${H.textMuted};font-size:16px;font-weight:800;font-family:var(--sans);cursor:pointer;border-radius:10px;display:flex;align-items:center;justify-content:center;transition:background .15s;}
.dc-num-btn:hover{background:${H.goldSoft};color:${H.goldDark};}
.dc-num-input{flex:1;min-width:0;background:${H.bg};border:1px solid ${H.border};padding:9px 10px;color:${H.dark};font-size:13.5px;font-weight:700;font-family:var(--sans);outline:none;text-align:center;border-radius:10px;transition:border-color .2s;
  -moz-appearance:textfield;}
.dc-num-input:focus{border-color:${H.gold};}
.dc-num-input::-webkit-outer-spin-button,.dc-num-input::-webkit-inner-spin-button{-webkit-appearance:auto;margin-left:4px;}
.dc-num-field-compact{margin-bottom:0;gap:4px;}
.dc-num-field-compact .dc-num-btn{width:30px;height:38px;font-size:14px;}
.dc-num-field-compact .dc-num-input{padding:9px 4px;font-size:12.5px;}

.dc-brand-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0;border:1px solid rgba(0,0,0,.08);}

.dc-extra-add{display:flex;gap:8px;margin-bottom:10px;}
.dc-extra-name{flex:1;background:${H.bg};border:1px solid ${H.border};padding:10px 12px;color:${H.dark};font-size:12.5px;font-family:var(--sans);outline:none;border-radius:10px;}
.dc-extra-price{width:64px;background:${H.bg};border:1px solid ${H.border};padding:10px 8px;color:${H.dark};font-size:12.5px;font-family:var(--sans);outline:none;text-align:center;border-radius:10px;}
.dc-extra-add-btn{width:40px;height:40px;background:${H.red};border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;border-radius:10px;transition:transform .15s;}
.dc-extra-add-btn:active{transform:translateY(1px);}
.dc-extra-list{display:flex;flex-direction:column;gap:6px;margin-bottom:6px;}
.dc-extra-row{display:flex;align-items:center;gap:8px;background:${H.bg};border:1px solid ${H.border};padding:9px 12px;border-radius:10px;}
.dc-extra-rname{flex:1;color:${H.dark};font-size:12.5px;font-weight:600;}
.dc-extra-rprice{color:${H.redDark};font-size:12.5px;font-weight:700;margin-right:8px;}
.dc-extra-rdel{background:none;border:none;cursor:pointer;color:${H.textFaint};display:flex;align-items:center;}
.dc-extra-rdel:hover{color:${H.red};}

.dc-toggle-row{display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid ${H.border};}
.dc-toggle-row:last-child{border-bottom:none;}
.dc-toggle-lbl{font-size:12.5px;font-weight:700;color:${H.dark};}
.dc-toggle-sub{font-size:10px;color:${H.textFaint};margin-top:1px;}
.dc-tog{width:42px;height:24px;border-radius:12px;background:${H.border};position:relative;cursor:pointer;transition:background .2s;flex-shrink:0;}
.dc-tog.on{background:${H.greenSoft};border:1px solid #BEE7CC;}
.dc-tog-thumb{position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 4px rgba(27,14,6,.28);transition:left .2s,background .2s;}
.dc-tog.on .dc-tog-thumb{left:20px;background:${H.green};}

/* delete confirm */
.dc-del-modal-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.5);z-index:400;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);padding:20px;}
.dc-del-modal{background:${H.panel};border:1px solid ${H.border};padding:24px;max-width:360px;width:100%;display:flex;flex-direction:column;gap:12px;border-radius:20px;box-shadow:0 30px 70px rgba(27,14,6,.3);}
.dc-del-modal-title{font-size:16px;font-weight:800;color:${H.dark};}
.dc-del-modal-sub{font-size:13px;color:${H.textMuted};line-height:1.6;}
.dc-del-modal-actions{display:flex;gap:10px;margin-top:4px;}
.dc-del-cancel{flex:1;padding:12px 0;background:${H.panelSoft};border:1px solid ${H.border};cursor:pointer;color:${H.textMuted};font-size:10.5px;font-weight:800;letter-spacing:.6px;font-family:var(--sans);border-radius:999px;}
.dc-del-cancel:hover{background:${H.border};}
.dc-del-confirm{flex:1;padding:12px 0;background:${H.red};border:none;cursor:pointer;color:#fff;font-size:10.5px;font-weight:800;letter-spacing:.6px;font-family:var(--sans);border-radius:999px;display:flex;align-items:center;justify-content:center;gap:6px;box-shadow:0 4px 12px rgba(200,32,46,.28);transition:transform .15s;}
.dc-del-confirm:hover{filter:brightness(1.06);}
.dc-del-confirm:active{transform:translateY(1px);}

@keyframes dcSpin{to{transform:rotate(360deg);}}
@keyframes dcSlideUp{from{transform:translateY(100%);}to{transform:translateY(0);}}
@keyframes dcPopIn{from{opacity:0;transform:scale(.94) translateY(8px);}to{opacity:1;transform:scale(1) translateY(0);}}
`;

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MAIN                                                                         */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function DriverCars() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((s: RootState) => s.auth);
  const hostId = (user as any)?.userId || (user as any)?.uid;
  const userId = (user as any)?.userId || (user as any)?.uid;
  // ✅ same commerce-partners slice used in ShortListings
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
  const commerceLoading  = useSelector((s: RootState) => s.commercePartners.loading);
  const rideBrandId = (commercePartners as any[]).find(
    (p) => Number(p.categoryId) === 2 && Number(p.subcategoryId) === 201
  )?.brandId;

  const { convertRaw, symbol } = useCurrency();

  /* ── State ── */
  // ✅ FIX: listings now come straight from the drivers redux slice, kept live
  // by listenToDriverData (queried on the `driverId` field — the old version
  // queried "userId"/"hostId", fields that don't exist on these documents,
  // which is why nothing ever showed up / seemed to "not save").
  const listings      = useSelector(selectListings);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [liveStatus,   setLiveStatus]   = useState<Record<string, { online: boolean; busy: boolean }>>({});
  const [editForm,     setEditForm]     = useState<EditForm>(blankForm());
  const [editingId,    setEditingId]    = useState<string | null>(null);
  const [step,         setStep]         = useState(0);
  const [saving,       setSaving]       = useState(false);
  const [delTarget,    setDelTarget]    = useState<any>(null);
  const [deleting,     setDeleting]     = useState(false);
  const [mounted,      setMounted]      = useState(false);
  const [newExtraName, setNewExtraName] = useState("");
  const [newExtraPrice,setNewExtraPrice]= useState("");
  const [search,        setSearch]      = useState("");
  const [editVisible,  setEditVisible]  = useState(false);
  const [expandedIds,  setExpandedIds]  = useState<Set<string>>(new Set());
  const [togglingIds,  setTogglingIds]  = useState<Set<string>>(new Set());

  const pingRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  /* CSS inject */
  useEffect(() => {
    const id = "dc-css-heartlas";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style"); tag.id = id; tag.textContent = CSS;
      document.head.appendChild(tag);
    }
    requestAnimationFrame(() => setMounted(true));
  }, []);

  /* ---------------- LISTEN TO THIS HOST'S VEHICLE LISTINGS ---------------- */
 /* ---------------- LISTEN TO COMMERCE PARTNERS ---------------- */
  useEffect(() => {
    if (!hostId) return;
    dispatch(listenToUserCommercePartners(hostId));
  }, [dispatch, hostId]);

  /* ---------------- LISTEN TO THIS HOST'S VEHICLE LISTINGS ---------------- */
  useEffect(() => {
    if (!hostId) return;
    setLoading(true);
    const unsub = listenToDriverData(hostId)(dispatch);
    setLoading(false);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch, hostId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await new Promise((r) => setTimeout(r, 400)); // listener is already live; this is just visual feedback
    setRefreshing(false);
  };

  /* RTDB live status */
  useEffect(() => {
    if (!listings.length) return;
    const ids = Array.from(new Set(listings.map((l: any) => l.listingId).filter(Boolean)));
    const refs = ids.map((id) => {
      const r = dbRef(rtdb, `onlineDrivers/${id}`);
      onValue(r, (snap) => {
        const v = snap.val();
        setLiveStatus((prev) => ({
          ...prev,
          [id]: v ? { online: true, busy: v.isAvailable === false } : { online: false, busy: false },
        }));
      });
      return r;
    });
    return () => refs.forEach((r) => off(r, "value"));
  }, [listings.map((l: any) => l.listingId).join(",")]);

  /* ---------------- OPEN / CLOSE EDIT WIZARD — fully inline, no routing ---------------- */
  // Card click opens the same modal in place, populated with that listing's
  // data (like editing a brand in CommerceOnboarding, just without changing
  // the URL). Add Vehicle opens it blank. Nothing here ever calls navigate().
  const openEdit = useCallback((listing?: any) => {
    if (listing) {
      setEditingId(listing.listingId);
      setEditForm(formFromListing(listing));
    } else {
      setEditingId(null);
      setEditForm(blankForm());
    }
    setStep(0);
    setEditVisible(true);
  }, []);

  const closeEdit = useCallback(() => {
    setEditVisible(false);
  }, []);

  const fc = useCallback((key: string, val: any) => setEditForm((p) => ({ ...p, [key]: val })), []);

  const stepValid = useCallback((s: number) => {
    if (s === 0) return editForm.model.trim().length > 0 && editForm.licensePlate.trim().length > 0;
    if (s === 2) return editForm.city.trim().length > 0;
    return true;
  }, [editForm]);

  /* upsertDriver's thunk signature is `{ listingId?: string; data: Partial<DriverProfile> }`.
     hostId and brandId are resolved inside the thunk itself — no need to pass them here. */
  const handleSave = useCallback(async () => {
    if (!hostId) return;
    if (!editForm.model.trim())        { alert("Vehicle model is required."); return; }
    if (!editForm.licensePlate.trim()) { alert("Licence plate is required."); return; }
    if (!editForm.city.trim())         { alert("City is required."); return; }
    setSaving(true);
    try {
      await (dispatch as any)(upsertDriver({
        listingId: editingId ?? undefined,
        data: {
          rideType:        editForm.rideTypeId,
          vehicleType:     editForm.vehicleTypeId,
          brand:           editForm.brandId,
          classType:       editForm.classTypeId,
          carSize:         editForm.carSizeId,
          fuelType:        editForm.fuelTypeId,
          transmission:    editForm.transmissionId,
          model:           editForm.model,
          licensePlate:    editForm.licensePlate,
          year:            editForm.year,
          capacity:        editForm.capacity,
          description:     editForm.description,
          rate:            editForm.rate,
          rateType:        editForm.rateType,
          extras:          extrasToObj(editForm.extras),
          discountApplied: buildDiscount(editForm.discountKey),
          extraCharges:    editForm.extras.reduce((s, e) => s + e.price, 0),
          guestCount:      parseInt(editForm.guestCount) || undefined,
          maxDays:         parseInt(editForm.maxDays)   || undefined,
          maxWeeks:        parseInt(editForm.maxWeeks)  || undefined,
          maxMonths:       parseInt(editForm.maxMonths) || undefined,
          maxYears:        parseInt(editForm.maxYears)  || undefined,
          city:            editForm.city,
          country:         editForm.country,
          serviceRadiusKm: parseFloat(editForm.serviceRadiusKm) || 15,
          allowParcel:     editForm.allowParcel,
          rideSharing:     editForm.rideSharing,
          isPublished:     editForm.isPublished,
          isDraft:         !editForm.isPublished,
        },
      })).unwrap();
      closeEdit();
    } catch (e: any) { alert(e.message ?? "Save failed. Please try again."); }
    finally { setSaving(false); }
  }, [hostId, editingId, editForm, dispatch, closeEdit]);

  /* Toggle publish */
  const togglePublish = useCallback(async (listing: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !listing.isPublished;
    try {
      await updateDoc(doc(db, "Fleet", listing.listingId), { isPublished: next, isDraft: !next, updatedAt: serverTimestamp() });
    } catch { /* listener will resync state either way */ }
  }, []);

  /* Tap a row to open it "like a book" and reveal the rest of its data */
  const toggleExpand = useCallback((listingId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(listingId)) next.delete(listingId); else next.add(listingId);
      return next;
    });
  }, []);

  
  /* Delete */
  const handleDelete = useCallback(async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteDriver(delTarget.listingId)).unwrap();
      setDelTarget(null);
    } catch (e: any) { alert(e.message); }
    finally { setDeleting(false); }
  }, [delTarget, dispatch]);

    const handleSetActive = useCallback((listing: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hostId || !userId || !listing?.listingId) return;
    if (listing.isSelected && listing.driverId === userId) return; // already active — no-op, don't deselect
    dispatch(setActiveVehicle({ hostId, listingId: listing.listingId, userId }));
  }, [dispatch, hostId, userId]);

  /* Online/offline toggle — same backend endpoints DriverDashboard uses to
     mark a vehicle available and start pinging its location every 8s. */
  const startPing = useCallback((id: string) => {
    if (pingRef.current[id]) clearInterval(pingRef.current[id]);
    const send = () => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => fetch(DRIVER_PING, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ driverId: id, lat: coords.latitude, lng: coords.longitude, isAvailable: true }),
        }).catch(() => {}),
        () => fetch(DRIVER_PING, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ driverId: id, lat: 0, lng: 0, isAvailable: true }),
        }).catch(() => {})
      );
    };
    send();
    pingRef.current[id] = setInterval(send, 8000);
  }, []);

  const stopPing = useCallback((id: string) => {
    if (pingRef.current[id]) { clearInterval(pingRef.current[id]); delete pingRef.current[id]; }
  }, []);

  const handleToggleOnline = useCallback(async (listing: any, lid: string, isOnline: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    const dId = listing.listingId;
    if (!dId || togglingIds.has(lid)) return;
    const next = !isOnline;
    setTogglingIds((prev) => new Set(prev).add(lid));
    try {
      await fetch(next ? DRIVER_ONLINE : DRIVER_OFFLINE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ driverId: dId }),
      });
      if (next) {
        await set(dbRef(rtdb, `onlineDrivers/${dId}`), { isAvailable: true, lat: 0, lng: 0, ts: Date.now() });
        startPing(dId);
      } else {
        await remove(dbRef(rtdb, `onlineDrivers/${dId}`));
        stopPing(dId);
      }
    } catch { /* liveStatus listener will resync either way */ }
    finally {
      setTogglingIds((prev) => { const n = new Set(prev); n.delete(lid); return n; });
    }
  }, [togglingIds, startPing, stopPing]);

  /* stop all pings if this page unmounts while a vehicle is online */
  useEffect(() => {
    return () => { Object.values(pingRef.current).forEach(clearInterval); };
  }, []);

  /* Add extra */
  const addExtra = () => {
    const name = newExtraName.trim();
    const price = parseFloat(newExtraPrice);
    if (!name || !price || price <= 0) return;
    fc("extras", [...editForm.extras, { name, price }]);
    setNewExtraName(""); setNewExtraPrice("");
  };

  const goNext = () => { if (stepValid(step)) setStep((s) => Math.min(STEPS.length - 1, s + 1)); };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  /* Search filter — user can have many vehicles; let them find one fast */
  const filteredListings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return listings;
    return listings.filter((l: any) => {
      const brandLabel = l.brand ? carBrandName(l.brand) : "";
      return [l.model, l.licensePlate, l.city, brandLabel, vehicleName(l.vehicleType)]
        .filter(Boolean)
        .some((f: string) => f.toLowerCase().includes(q));
    });
  }, [listings, search]);

  /* ─────────────────────────────────────────────────────────────────────── */
  /* STEP CONTENT                                                             */
  /* ─────────────────────────────────────────────────────────────────────── */
  const StepVehicle = () => {
    const typeOptions   = vehicleTypesForRideType(editForm.rideTypeId);
    const selectedBrand = (CAR_BRANDS as any[]).find((b) => b.id === editForm.brandId);

    return (
    <>
      <div className="dc-step-eye"><Car size={12} /> Tell us about the vehicle</div>

      <PickerField
        label="Ride Type"
        value={editForm.rideTypeId}
        options={RIDE_TYPES.map((r: any) => ({ id: r.id, name: r.name, icon: r.icon }))}
        onSelect={(id) => {
          fc("rideTypeId", id);
          const next = vehicleTypesForRideType(id);
          if (next.length) fc("vehicleTypeId", next[0].id);
        }}
        required
      />

      {typeOptions.length > 0 && (
        <PickerField
          label="Type"
          value={editForm.vehicleTypeId}
          options={typeOptions.map((v: any) => ({ id: v.id, name: v.name }))}
          onSelect={(id) => fc("vehicleTypeId", id)}
          required
        />
      )}

      <PickerField
        label="Brand"
        value={editForm.brandId}
        options={(CAR_BRANDS as any[]).map((b) => ({
          id: b.id, name: b.name, color: b.color,
          badge: b.classType !== CLASS_TYPE.ECONOMY ? classTypeName(b.classType) : undefined,
        }))}
        onSelect={(id) => {
          fc("brandId", id);
          const b = (CAR_BRANDS as any[]).find((x) => x.id === id);
          if (b) fc("classTypeId", b.classType);
        }}
        required
      />
      {selectedBrand && selectedBrand.classType !== editForm.classTypeId && (
        <div style={{ fontSize: 10, color: H.textFaint, marginTop: -8, marginBottom: 12 }}>
          {selectedBrand.name} is usually listed as {classTypeName(selectedBrand.classType)}
        </div>
      )}

      <PickerField
        label="Class"
        value={editForm.classTypeId}
        options={CLASS_TYPES.map((c: any) => ({ id: c.id, name: c.name, icon: c.icon, color: c.color }))}
        onSelect={(id) => fc("classTypeId", id)}
      />

      <PickerField
        label="Size"
        value={editForm.carSizeId}
        options={CAR_SIZES.map((s: any) => ({ id: s.id, name: s.name }))}
        onSelect={(id) => fc("carSizeId", id)}
      />

      <div className="dc-form-row">
        <div>
          <label className="dc-form-lbl">Make / Model *</label>
          <input className="dc-input" value={editForm.model} onChange={(e) => fc("model", e.target.value)} placeholder="Toyota Camry" />
        </div>
        <div>
          <label className="dc-form-lbl">Year</label>
          <NumberStepper value={editForm.year} onChange={(v) => fc("year", v)} min={1950} max={2100} step={1} placeholder="2022" compact />
        </div>
      </div>

      <div className="dc-form-row">
        <div>
          <label className="dc-form-lbl">Licence Plate *</label>
          <input className="dc-input" value={editForm.licensePlate} onChange={(e) => fc("licensePlate", e.target.value.toUpperCase())} placeholder="KCA 123A" />
        </div>
        <div>
          <label className="dc-form-lbl">Seats</label>
          <NumberStepper value={editForm.capacity} onChange={(v) => fc("capacity", v)} min={1} max={99} step={1} placeholder="4" compact />
        </div>
      </div>

      <PickerField
        label="Fuel"
        value={editForm.fuelTypeId}
        options={(FUEL_TYPES as any[]).map((f) => ({ id: f.id, name: f.name }))}
        onSelect={(id) => fc("fuelTypeId", id)}
      />

      <PickerField
        label="Transmission"
        value={editForm.transmissionId}
        options={(TRANSMISSION_TYPES as any[]).map((t) => ({ id: t.id, name: t.name }))}
        onSelect={(id) => fc("transmissionId", id)}
      />

      <label className="dc-form-lbl">Description</label>
      <textarea className="dc-textarea" rows={3} value={editForm.description} onChange={(e) => fc("description", e.target.value)} placeholder="Clean, comfortable ride…" />
    </>
    );
  };

  const StepPricing = () => (
    <>
      <div className="dc-step-eye"><DollarSign size={12} /> Set your pricing</div>

      <PickerField
        label="Billing Type"
        value={editForm.rateType}
        options={RATE_OPTS.map((r) => ({ id: r.type, name: r.label, sub: r.sub, badge: r.type === "perKm" ? "Recommended" : undefined }))}
        onSelect={(id) => fc("rateType", id)}
      />

      {rateCategoryOf(editForm.rateType) === "people" && (
        <>
          <label className="dc-form-lbl">
            {editForm.rateType === "perSeat" ? "Max Seats You'll Sell" : "Max People Per Ride"}
          </label>
          <NumberStepper
            value={editForm.guestCount}
            onChange={(v) => fc("guestCount", v)}
            min={1}
            max={99}
            step={1}
            placeholder="4"
          />
          <div style={{ fontSize: 10, color: H.textFaint, marginTop: -8, marginBottom: 12 }}>
            Riders will be able to pick up to this many {editForm.rateType === "perSeat" ? "seats" : "people"} when booking.
          </div>
        </>
      )}

      {rateCategoryOf(editForm.rateType) === "duration" && (() => {
        const unit = DURATION_UNIT_LABEL[editForm.rateType] ?? "day";
        const key = DURATION_FIELD_KEY[editForm.rateType] ?? "maxDays";
        return (
          <>
            <label className="dc-form-lbl">Max {unit}s Per Booking</label>
            <NumberStepper
              value={(editForm as any)[key]}
              onChange={(v) => fc(key, v)}
              min={1}
              max={key === "maxYears" ? 10 : key === "maxMonths" ? 60 : key === "maxWeeks" ? 104 : 365}
              step={1}
              placeholder="30"
            />
            <div style={{ fontSize: 10, color: H.textFaint, marginTop: -8, marginBottom: 12 }}>
              The longest a rider can book this vehicle for, in {unit}s.
            </div>
          </>
        );
      })()}

      <label className="dc-form-lbl">Base Rate</label>
      <div className="dc-rate-big">
        <span className="dc-rate-big-curr">{symbol}</span>
        <span className="dc-rate-big-val">{Number(convertRaw(editForm.rate, "EUR")).toLocaleString()}</span>
        <span className="dc-rate-big-unit">{rateUnit(editForm.rateType)}</span>
      </div>
      <div className="dc-stepper">
        {[-50, -10, -1].map((d) => (
          <button key={d} className="dc-step-btn" onClick={() => fc("rate", Math.max(1, editForm.rate + d))}>{d}</button>
        ))}
        <input className="dc-step-input" value={editForm.rate} onChange={(e) => fc("rate", Math.max(1, parseInt(e.target.value) || 1))} />
        {[+1, +10, +50].map((d) => (
          <button key={d} className="dc-step-btn" onClick={() => fc("rate", Math.min(100000, editForm.rate + d))}>+{d}</button>
        ))}
      </div>

      <label className="dc-form-lbl">Discount</label>
      <div className="dc-chips">
        {DISC_OPTS.map((d) => (
          <button key={d.key} className={`dc-chip${editForm.discountKey === d.key ? " on" : ""}`} onClick={() => fc("discountKey", d.key)}>{d.label}</button>
        ))}
      </div>

      <label className="dc-form-lbl">Extra Charges</label>
      <div className="dc-extra-add">
        <input className="dc-extra-name" placeholder="Name (e.g. WiFi)" value={newExtraName} onChange={(e) => setNewExtraName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addExtra()} />
        <input className="dc-extra-price" type="number" inputMode="decimal" placeholder={symbol} value={newExtraPrice} onChange={(e) => setNewExtraPrice(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addExtra()} />
        <button className="dc-extra-add-btn" onClick={addExtra}><Plus size={16} /></button>
      </div>
      {editForm.extras.length > 0 && (
        <div className="dc-extra-list">
          {editForm.extras.map((e, i) => (
            <div key={i} className="dc-extra-row">
              <span className="dc-extra-rname">{e.name}</span>
              <span className="dc-extra-rprice">{symbol}{Number(convertRaw(e.price, "EUR")).toLocaleString()}</span>
              <button className="dc-extra-rdel" onClick={() => fc("extras", editForm.extras.filter((_, idx) => idx !== i))}><X size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const StepLocation = () => (
    <>
      <div className="dc-step-eye"><MapPin size={12} /> Where you operate</div>

      <div className="dc-form-row">
        <div>
          <label className="dc-form-lbl">City *</label>
          <input className="dc-input" value={editForm.city} onChange={(e) => fc("city", e.target.value)} placeholder="Nairobi" />
        </div>
        <div>
          <label className="dc-form-lbl">Country</label>
          <input className="dc-input" value={editForm.country} onChange={(e) => fc("country", e.target.value.toUpperCase())} placeholder="KE" maxLength={2} />
        </div>
      </div>
      <label className="dc-form-lbl">Service Radius (km)</label>
      <NumberStepper value={editForm.serviceRadiusKm} onChange={(v) => fc("serviceRadiusKm", v)} min={1} max={500} step={5} placeholder="15" />

      <label className="dc-form-lbl" style={{ marginTop: 4 }}>Capabilities</label>
      {[
        { key: "allowParcel", label: "Accept Parcel Deliveries", sub: "Riders can request deliveries with this vehicle" },
        { key: "rideSharing", label: "Accept Shared Rides",      sub: "Multiple passengers can split one trip" },
        { key: "isPublished", label: "Publish Listing",          sub: "Make this vehicle visible to riders" },
      ].map(({ key, label, sub }) => (
        <div key={key} className="dc-toggle-row">
          <div>
            <div className="dc-toggle-lbl">{label}</div>
            <div className="dc-toggle-sub">{sub}</div>
          </div>
          <div className={`dc-tog${(editForm as any)[key] ? " on" : ""}`} onClick={() => fc(key, !(editForm as any)[key])}>
            <div className="dc-tog-thumb" />
          </div>
        </div>
      ))}
    </>
  );

  // Called as plain functions (StepVehicle(), not <StepVehicle/>) — rendering
  // as JSX components would give React a "new" component type every render,
  // forcing an unmount/remount of the inputs and dropping focus after each
  // keystroke (the "only one letter at a time" bug).
  const STEP_CONTENT = [StepVehicle(), StepPricing(), StepLocation()];

  /* ─────────────────────────────────────────────────────────────────────── */
  /* RENDER                                                                   */
  /* ─────────────────────────────────────────────────────────────────────── */
  const liveCount    = listings.filter((l: any) => l.isPublished).length;
  const onlineCount  = listings.filter((l: any) => liveStatus[l.listingId]?.online).length;
  const isLastStep   = step === STEPS.length - 1;

  return (
    <div className={`dc-root${mounted ? " dc-on" : ""}`}>

      {/* HEADER */}
      <div className="dc-header dc-rev" style={{ "--d": "0ms" } as any}>
        <div className="dc-header-text">
          <div className="dc-header-eye">My Fleet</div>
          <div className="dc-header-title">Listings</div>
        </div>
        <button className="dc-icon-btn" onClick={handleRefresh} title="Refresh">
          <RefreshCw size={14} className={refreshing ? "dc-spin" : ""} />
        </button>
        <button className="dc-add-btn" onClick={() => openEdit()}>
          <Plus size={14} /> Add Vehicle
        </button>
      </div>

      {/* SEARCH — lets a driver with many vehicles find one fast */}
      {!loading && listings.length > 0 && (
        <div className="dc-search dc-rev" style={{ "--d": "40ms" } as any}>
          <Search size={14} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by model, plate, brand, or city…"
          />
          {search && (
            <button className="dc-search-clear" onClick={() => setSearch("")} aria-label="Clear search">
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* STATS STRIP */}
      {!loading && listings.length > 0 && (
        <div className="dc-stats dc-rev" style={{ "--d": "60ms" } as any}>
          {[
            { label: "Total",  val: String(listings.length), color: H.goldDark },
            { label: "Live",   val: String(liveCount),        color: "#15803D" },
            { label: "Online", val: String(onlineCount),       color: H.blue },
          ].map(({ label, val, color }) => (
            <div key={label} className="dc-stat-item">
              <div className="dc-stat-val" style={{ color }}>{val}</div>
              <div className="dc-stat-lbl">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* LIST */}
      {loading ? (
        <div className="dc-loader">
          <Loader2 size={30} className="dc-spin" color={H.goldDark} />
          <div className="dc-loader-txt">Loading your listings…</div>
        </div>
      ) : listings.length === 0 ? (
        <div className="dc-empty dc-rev" style={{ "--d": "80ms" } as any}>
          <div className="dc-empty-icon"><Car size={44} /></div>
          <div className="dc-empty-title">No listings yet</div>
          <div className="dc-empty-sub">Add your first vehicle so riders can find and book you.</div>
          <button className="dc-empty-btn" onClick={() => openEdit()}><Plus size={13} /> Create First Listing</button>
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="dc-empty dc-rev" style={{ "--d": "80ms" } as any}>
          <div className="dc-empty-icon"><Search size={40} /></div>
          <div className="dc-empty-title">No matches</div>
          <div className="dc-empty-sub">Nothing matches "{search}". Try a different search.</div>
        </div>
      ) : (
        <div className="dc-list">
          {filteredListings.map((listing: any, i: number) => {
            const lid      = listing.listingId || listing.hostId || `row-${i}`;
            const live     = liveStatus[lid] ?? null;
            const isOnline = live?.online ?? false;
            const isBusy   = live?.busy ?? false;
            const extArr   = extrasToArr(listing.extras);
            const discKey  = discKeyFromObj(listing.discountApplied);
            const statusColor = isOnline ? (isBusy ? H.amber : "#15803D") : H.textFaint;
            const statusLabel = isOnline ? (isBusy ? "Busy" : "Online") : "Offline";
            const expanded = expandedIds.has(lid);

            return (
              <div
                key={lid}
                className={`dc-row-card dc-rev${listing.isSelected ? " dc-card-active" : ""}`}
                style={{ "--d": `${80 + i * 50}ms` } as any}
              >
                {/* ── thumbnail — ride-type image with icon fallback (RowThumb),
                    live-status pill + active dot + publish toggle floating on top ── */}
                <div className="dc-row-head" onClick={() => toggleExpand(lid)}>
                  <div className="dc-row-thumb">
                    <RowThumb listing={listing} />
                    {listing.isSelected && <div className="dc-row-active-dot" title="Active vehicle" />}
                  </div>

                  <div className="dc-row-meta">
                    <div className="dc-row-title">
                      {listing.brand ? `${carBrandName(listing.brand)} ` : ""}{listing.model || vehicleName(listing.vehicleType)}
                    </div>
                    <div className="dc-row-sub">
                      {listing.licensePlate || "No plate set"}
                      {listing.city ? ` · ${listing.city}` : ""}
                    </div>
                    <div className="dc-status-pill">
                      <span className="dc-status-pill-dot" style={{ background: statusColor }} />
                      {statusLabel}
                    </div>
                  </div>

                  <div className="dc-row-side">
                    <div className="dc-row-rate">
                      {Number.isFinite(listing.rate)
                        ? `${symbol}${Number(convertRaw(listing.rate, "EUR")).toLocaleString()}`
                        : "—"}
                    </div>
                    <div className="dc-row-rate-unit">{rateUnit(listing.rateType)}</div>
                    <div className={`dc-row-chevron${expanded ? " open" : ""}`}><ChevronDown size={16} /></div>
                  </div>
                </div>

                {/* ── expand panel — opens "like a book": labelled specs, capability
                    tags, extras, description, location, active-vehicle switch,
                    edit/delete. Stays inside the card (overflow:hidden on the
                    card itself), so it never spills onto the rest of the grid. ── */}
                <div className={`dc-row-expand-wrap${expanded ? " open" : ""}`}>
                  <div className="dc-row-expand-inner">
                    <div className="dc-row-expand-page">

                      {/* real, labelled vehicle facts — not just badges */}
                      <div className="dc-spec-grid">
                        <SpecItem label="Type"         value={vehicleName(listing.vehicleType)} />
                        <SpecItem label="Fuel"         value={fuelTypeName(listing.fuelType)} />
                        <SpecItem label="Transmission" value={transmissionName(listing.transmission)} />
                        <SpecItem label="Class"        value={classTypeName(listing.classType)} />
                        <SpecItem label="Size"         value={carSizeName(listing.carSize)} />
                        <SpecItem label="Seats"        value={listing.capacity ? String(listing.capacity) : "—"} />
                        <SpecItem label="Year"         value={listing.year ? String(listing.year) : "—"} />
                      </div>

                      <div className="dc-meta-row">
                        {(listing.photos?.length || 0) > 1 && (
                          <div className="dc-pill" style={{ borderColor: H.border, background: H.bg, color: H.textMuted }}>
                            <ImageIcon size={9} /> {listing.photos.length} photos
                          </div>
                        )}
                        {listing.rideSharing && (
                          <div className="dc-pill" style={{ borderColor: H.blue + "44", background: H.blueSoft, color: H.blue }}>
                            <GitMerge size={9} /> Sharing
                          </div>
                        )}
                        {listing.allowParcel && (
                          <div className="dc-pill" style={{ borderColor: H.amber + "44", background: H.amberSoft, color: H.amber }}>
                            <Package size={9} /> Parcels
                          </div>
                        )}
                        {discKey !== "none" && (
                          <div className="dc-pill" style={{ borderColor: H.gold + "55", background: H.goldSoft, color: H.goldDark }}>
                            <Tag size={9} /> {DISC_OPTS.find((d) => d.key === discKey)?.label} off
                          </div>
                        )}
                      </div>

                      {listing.description && (
                        <div className="dc-desc-row">{listing.description}</div>
                      )}

                      {extArr.length > 0 && (
                        <div className="dc-extras-row">
                          {extArr.map((e) => (
                            <span key={e.name} className="dc-extra-tag">
                              +{e.name} {symbol}{Number(convertRaw(e.price, "EUR")).toLocaleString()}
                            </span>
                          ))}
                        </div>
                      )}

                      {listing.city && (
                        <div className="dc-loc-row">
                          <MapPin size={11} />
                          {listing.city}{listing.country ? `, ${listing.country}` : ""}
                          {listing.serviceRadiusKm ? ` · ${listing.serviceRadiusKm}km radius` : ""}
                        </div>
                      )}

                      {/* Uber-Fleet-style: pick which vehicle you're driving right now.
                          Selecting writes isSelected: true to this listing in the DB
                          (and false to every sibling vehicle) via setActiveVehicle. */}
                      <div className="dc-active-row">
                        <button className="dc-active-btn" onClick={(e) => handleSetActive(listing, e)}>
                          <div className={`dc-active-circle${listing.isSelected ? " on" : ""}`} />
                          <span className={`dc-active-label${listing.isSelected ? " on" : ""}`}>
                            {listing.isSelected ? "Active Vehicle" : "Set as Active"}
                          </span>
                        </button>
                      </div>

                      {/* Online/offline — calls the same driverOnline/driverOffline
                          backend as DriverDashboard and starts/stops the 8s ping. */}
                      <div className="dc-toggle-row">
                        <div>
                          <div className="dc-toggle-lbl" style={{ color: togglingIds.has(lid) ? H.textFaint : isOnline ? "#15803D" : H.dark }}>
                            {togglingIds.has(lid) ? "Updating…" : isOnline ? "Online — accepting rides" : "Go online"}
                          </div>
                          <div className="dc-toggle-sub">
                            {isOnline ? "Pinging location every 8s" : "Toggle to accept rides with this vehicle"}
                          </div>
                        </div>
                        <div
                          className={`dc-tog${isOnline ? " on" : ""}`}
                          style={togglingIds.has(lid) ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                          onClick={(e) => handleToggleOnline(listing, lid, isOnline, e)}
                        >
                          <div className="dc-tog-thumb" />
                        </div>
                      </div>

                      <div className="dc-card-actions">
                        <button className="dc-edit-btn" onClick={(e) => { e.stopPropagation(); openEdit(listing); }}>
                          <Edit3 size={13} /> EDIT
                        </button>
                        <button className="dc-del-btn" onClick={(e) => { e.stopPropagation(); setDelTarget(listing); }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── ADD / EDIT MODAL — floating card (desktop) / bottom sheet (mobile) ── */}
      {editVisible && (
        <div className="dc-modal-backdrop" onClick={() => !saving && closeEdit()}>
          <div className="dc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dc-mob-handle" />

            <div className="dc-modal-header">
              <div>
                <div className="dc-modal-title">{editingId ? "Edit Vehicle" : "Add Vehicle"}</div>
              </div>
              <button className="dc-modal-close" onClick={closeEdit}><X size={15} /></button>
            </div>

            <div className="dc-steps">
              {STEPS.map((s, i) => (
                <div key={s.key} className="dc-step-dot" style={{ flex: i === STEPS.length - 1 ? "0 0 auto" : 1 }}>
                  <div className={`dc-step-circle${i < step ? " done" : i === step ? " active" : ""}`}>
                    {i < step ? <CheckCircle size={13} /> : i + 1}
                  </div>
                  {i < STEPS.length - 1 && <div className={`dc-step-line${i < step ? " done" : ""}`} />}
                </div>
              ))}
            </div>

            <div className="dc-modal-scroll">
              {STEP_CONTENT[step]}
            </div>

            <div className="dc-modal-footer">
              {step > 0 && (
                <button className="dc-btn-ghost" onClick={goBack}>
                  <ChevronLeft size={14} /> Back
                </button>
              )}
              {!isLastStep ? (
                <button className="dc-btn-primary" onClick={goNext} disabled={!stepValid(step)}>
                  Next <ChevronRight size={14} />
                </button>
              ) : (
                <button className="dc-btn-primary" onClick={handleSave} disabled={saving || !stepValid(2)}>
                  {saving ? <Loader2 size={14} className="dc-spin" /> : <><Sparkles size={14} /> Save Listing</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {delTarget && (
        <div className="dc-del-modal-backdrop" onClick={() => !deleting && setDelTarget(null)}>
          <div className="dc-del-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: H.red }}>
              <AlertCircle size={20} />
              <div className="dc-del-modal-title">Delete Listing</div>
            </div>
            <div className="dc-del-modal-sub">
              Are you sure you want to delete <strong style={{ color: H.dark }}>"{delTarget.model || "this listing"}"</strong>? This cannot be undone.
            </div>
            <div className="dc-del-modal-actions">
              <button className="dc-del-cancel" onClick={() => setDelTarget(null)} disabled={deleting}>Cancel</button>
              <button className="dc-del-confirm" onClick={handleDelete} disabled={deleting}>
                {deleting ? <Loader2 size={13} className="dc-spin" /> : <Trash2 size={13} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}